import {
  createFeatureSelector,
  createSelector,
  MemoizedSelector,
} from '@ngrx/store';
import {
  CombinedLongeingData,
  DbStatus,
  LongeingDirectionEnum,
  LongeingDirectionType,
  Recording,
  RecordingActionSuccessTypeEnum,
  RecordingStatusEnum,
  ResnetRecording,
} from '@sleipai/sleip-models';
import { State, recordingFeatureKey } from '../reducers/recording.reducer';
import * as fromRouterSelector from '../../router-store/router.selectors';
import {
  dynamicSort,
  filterReocringsList,
  PAGE_SIZE,
  QueryConfig,
} from '../../helpers';
import { selectAllRecordingAdapter } from '../reducers/recording.reducer';

export const selectRecordingState =
  createFeatureSelector<State>(recordingFeatureKey);

export const isLoading = (state: State): boolean => state.loading;

export const isListLoading = (state: State): boolean => state.loadingList;
export const isListCountLoading = (state: State): boolean =>
  state.loadingListCount;

export const isLoaded = (state: State): boolean => state.loaded;
export const recordingError = (state: State): string | null => state.error;
export const recordingListOffset = (state: State): any => state.offset;
export const recordingListStatus = (state: State): string =>
  state.reviewStatus ?? '';
export const totalRecordingsCount = (state: State): number =>
  state.totalRecordingsCount;
export const recordingRemoved = (state: State): boolean =>
  state.recordingRemoved;
export const recordingUpdating = (state: State): boolean =>
  state.updatingRecording;
export const recordingUpdated = (state: State): boolean =>
  state.updatedRecording;
export const hasMore = (state: State): boolean => state.hasMore;
export const recordingActionSuccessType = (
  state: State,
): RecordingActionSuccessTypeEnum | null => state.recordingActionSuccessType;
export const batchMoveFailedIds = (state: State): string[] | null => state.batchMoveFailedIds;
export const batchRemoveFailedIds = (state: State): string[] | null => state.batchRemoveFailedIds;

export const selectIsLoading: MemoizedSelector<object, boolean> =
  createSelector(selectRecordingState, isLoading);

export const selectIsListLoading: MemoizedSelector<object, boolean> =
  createSelector(selectRecordingState, isListLoading);
export const selectIsListCountLoading: MemoizedSelector<object, boolean> =
  createSelector(selectRecordingState, isListCountLoading);

export const selectIsLoaded: MemoizedSelector<object, boolean> = createSelector(
  selectRecordingState,
  isLoaded,
);
export const selectRecordingListOffset: MemoizedSelector<object, any> =
  createSelector(selectRecordingState, recordingListOffset);
export const selectRecordingListStatus: MemoizedSelector<object, string> =
  createSelector(selectRecordingState, recordingListStatus);

export const selectTotalRecordingsCount: MemoizedSelector<object, number> =
  createSelector(selectRecordingState, totalRecordingsCount);

export const selectRecordingRemoved: MemoizedSelector<object, boolean> =
  createSelector(selectRecordingState, recordingRemoved);

export const selectRecordingUpdating: MemoizedSelector<object, boolean> =
  createSelector(selectRecordingState, recordingUpdating);

export const selectRecordingUpdated: MemoizedSelector<object, boolean> =
  createSelector(selectRecordingState, recordingUpdated);

export const selectRecordingError: MemoizedSelector<object, string | null> =
  createSelector(selectRecordingState, recordingError);

export const selectAllRecordings: (state: object) => any[] = createSelector(
  selectRecordingState,
  selectAllRecordingAdapter,
);

export const selectSingleRecording = (passedRecordingId?: string) =>
  createSelector(
    selectAllRecordings,
    fromRouterSelector.selectRouteNestedParam('recordingId'),
    (recordings: Recording[], urlParamRecordingId: string) => {
      const recordingId = passedRecordingId ?? urlParamRecordingId;

      if (recordings) {
        return (
          recordings.find((recording) => recording.id === recordingId) ?? null
        );
      } else {
        return null;
      }
    },
  );

export const selectMultipleRecordings = (ids: string[]) =>
  createSelector(selectAllRecordings, (recordings: Recording[]) => {
    if (recordings) {
      const filteredRecordings = recordings.filter(({ id }) => {
        return ids.indexOf(id ?? '') != -1;
      });

      return filteredRecordings;
    } else {
      return null;
    }
  });

export const selectRecordingVideoFiles = (id: string) =>
  createSelector(selectAllRecordings, (recordings: ResnetRecording[]) => {
    if (recordings.length) {
      const recording: ResnetRecording | undefined = recordings.find(
        (rec: ResnetRecording) => rec.id === id,
      );
      return recording?.videofiles ?? null;
    } else {
      return null;
    }
  });

export const selectRecordingsByType = (dbStatus: string) =>
  createSelector(
    selectAllRecordings,
    selectRecordingListOffset,
    fromRouterSelector.selectRouteNestedParam('recordingType'),
    (recordings: Recording[], offset: Date, recordingType: string) => {
      if (recordings.length) {
        let recordingList: Recording[] =
          recordingType !== 'removed'
            ? recordings.filter((recording) => {
                return (
                  recording.dbStatus === dbStatus &&
                  recording.status === recordingType
                );
              })
            : recordings.filter((recording) => {
                return recording.dbStatus === 'removed';
              });
        recordingList = recordingList.sort((a, b) => {
          if (a.creationDate && b.creationDate) {
            return a.creationDate > b.creationDate ? -1 : 1;
          }
          return -1;
        });
        const limit = 10;
        if (offset) {
          const indexToStartAfter =
            recordingList.findIndex((rec) => rec.creationDate === offset) + 1;
          const currArrayLength = recordingList.length;
          if (currArrayLength === indexToStartAfter && currArrayLength !== 0) {
            return recordingList;
          } else if (currArrayLength === indexToStartAfter) {
            return [];
          } else {
            return recordingList.slice(0, indexToStartAfter + limit);
          }
        }
        return recordingList.length ? recordingList : [];
      } else {
        return [];
      }
    },
  );

export const selectRecordingsListPage = (
  queryConfig: QueryConfig,
  horseId?: string,
) => {
  const {
    page,
    orderBy = 'creationDate',
    orderDirection = 'asc',
    pageSize = PAGE_SIZE,
    filterMap,
  } = queryConfig;
  const offset = page * pageSize;
  const analysTypes = new Set<string>([...filterReocringsList(filterMap)]);
  const hasAnalgesia = filterMap?.get('hasAnalgesia');
  return createSelector(selectAllRecordings, (recordings: Recording[]) => {
    recordings = recordings.filter(
      (recording) => recording.dbStatus === DbStatus.ACTIVE,
    );
    if (analysTypes.size > 0)
      recordings = recordings.filter((recording) => {
        return analysTypes.has(recording.analysisType);
      });
    if (horseId)
      recordings = recordings.filter(
        (recording) => recording.horseId === horseId,
      );
    if (hasAnalgesia)
      recordings = recordings.filter(
        (recording) => recording.analgesia.hasAnalgesia === hasAnalgesia,
      );

    if (recordings.length > offset) {
      recordings = recordings.sort(dynamicSort(orderBy, orderDirection));
      if (page === 0) {
        return recordings.slice(0, pageSize);
      } else {
        return recordings.splice(offset, pageSize);
      }
    } else {
      return [];
    }
  });
};

export const selectRecordingsListPageForInfiniteScrollInDashBoard = (
  queryConfig: QueryConfig,
) => {
  const {
    page,
    orderBy = 'creationDate',
    orderDirection = 'asc',
    pageSize = PAGE_SIZE,
    filterMap,
  } = queryConfig;
  return createSelector(
    selectAllRecordings,
    selectIsListLoading,
    (recordings: Recording[], isListLoading: boolean) => {
      if (isListLoading || !recordings || recordings.length === 0) {
        return [];
      }
      let filteredRecordings = [...recordings];
      // -- Filter start

      const allRecordings = filterMap?.get('all');
      const horseId = filterMap?.get('horseId');
      const recordingOwnerId = filterMap?.get('recordingOwnerId');
      const recordingUploadedBy = filterMap?.get('recordingUploadedBy');


      if (horseId) {
        filteredRecordings = filteredRecordings.filter(
          (recording) => recording.horseId === horseId,
        );
      }

      if(recordingUploadedBy) {
        filteredRecordings = filteredRecordings.filter(
          (recording) => recording.uploadedBy === recordingUploadedBy,
        );
      }

      if (recordingOwnerId) {
        filteredRecordings = filteredRecordings.filter(
          (recording) => recording.owner.id === recordingOwnerId,
        );
      }

      if (!allRecordings) {
        const isRemoved = filterMap?.get('removed');
        if (isRemoved) {
          filteredRecordings = filteredRecordings.filter(
            (recording) => recording.dbStatus === DbStatus.REMOVED,
          );
        }

        const hasAnalgesia = filterMap?.get('hasAnalgesia');
        if (hasAnalgesia) {
          filteredRecordings = filteredRecordings.filter(
            (recording) => recording.analgesia.hasAnalgesia === true,
          );
        }

        const analysisTypesFilter = filterReocringsList(filterMap);
        if (analysisTypesFilter && analysisTypesFilter.length > 0) {
          filteredRecordings = filteredRecordings.filter((recording) =>
            analysisTypesFilter.includes(recording.analysisType),
          );
        }
      }

      // -- Start Sorting
      const sortedRecordings = [...filteredRecordings].sort(
        dynamicSort(orderBy, orderDirection),
      );

      // -- Start Pagination
      const endIndex = pageSize * (page + 1);
      return sortedRecordings.slice(0, endIndex);
    },
  );
};

export const selectRecordingsHasMore = createSelector(
  selectRecordingState,
  hasMore,
);

export const selectRecordingsActionSuccessType = createSelector(
  selectRecordingState,
  recordingActionSuccessType,
);

export const selectBatchMoveFailedIds = createSelector(
  selectRecordingState,
  batchMoveFailedIds,
);

export const selectBatchRemoveFailedIds = createSelector(
  selectRecordingState,
  batchRemoveFailedIds,
);

export const selectEligibleRecordingsToLinkForARecording = (
  horseId: string,
  recordingId: string,
  creationDate: Date,
  longeingDirection: LongeingDirectionType,
) =>
  createSelector(selectAllRecordings, (recordings: Recording[]) => {
    const oneDayInMs = 24 * 60 * 60 * 1000;
    const startDate = new Date(creationDate.getTime() - oneDayInMs);
    const endDate = new Date(creationDate.getTime() + oneDayInMs);

    if (recordings) {
      return (
        recordings
          .filter(
            (recording) =>
              recording.id !== recordingId &&
              recording.horseId === horseId &&
              recording.dbStatus === DbStatus.ACTIVE &&
              recording.status === RecordingStatusEnum.COMPLETE &&
              recording.creationDate !== null &&
              new Date(recording.creationDate.getTime()) > startDate &&
              new Date(recording.creationDate.getTime()) < endDate &&
              (recording.longeingData?.linkedRecordingId === null ||
                recording.longeingData?.linkedRecordingId === undefined) &&
              !recording.linkedRecordings?.sources.some(
                (linkedItem) => linkedItem.id === recordingId,
              ),
          )
          .filter((recording) => {
            if (longeingDirection === LongeingDirectionEnum.CLOCKWISE) {
              return (
                recording.longeingData?.direction ===
                  LongeingDirectionEnum.COUNTER_CLOCKWISE ||
                recording.longeingData?.direction ===
                  LongeingDirectionEnum.ANTI_CLOCKWISE
              );
            } else {
              return (
                recording.longeingData?.direction ===
                LongeingDirectionEnum.CLOCKWISE
              );
            }
          }) ?? null
      );
    } else {
      return null;
    }
  });

export const selectIsRecordingEligibleToBeLinkedForCombinedView = (
  horseId: string,
  recordingId: string,
  creationDate: Date,
  longeingDirection: LongeingDirectionType,
) =>
  createSelector(selectAllRecordings, (recordings: Recording[]) => {
    const oneDayInMs = 24 * 60 * 60 * 1000;
    const startDate = new Date(creationDate.getTime() - oneDayInMs);
    const endDate = new Date(creationDate.getTime() + oneDayInMs);
    if (recordings) {
      const eligibleRecordings = recordings.filter(
        (recording) =>
          recording.id !== recordingId &&
          recording.horseId === horseId &&
          recording.longeingData?.direction !== longeingDirection &&
          recording.dbStatus === DbStatus.ACTIVE &&
          recording.status === RecordingStatusEnum.COMPLETE &&
          recording.creationDate !== null &&
          recording.creationDate > startDate &&
          recording.creationDate < endDate &&
          (recording.longeingData?.linkedRecordingId === null ||
            recording.longeingData?.linkedRecordingId === undefined),
      );

      return eligibleRecordings.length > 0;
    } else {
      return false;
    }
  });

export const selectAllCombinedLongeings: MemoizedSelector<object, CombinedLongeingData[]> =
  createSelector(
    selectRecordingState,
    (state: State) => state.combinedLongeings ?? []
  );

export const selectCombinedLongeingById = (combinedId: string) =>
  createSelector(selectAllCombinedLongeings, (combinedList: CombinedLongeingData[]) => {
    return combinedList.find((item) => item.combinedId === combinedId) ?? null;
  });

export const selectCombinedLongeingByRecordingId = (recordingId: string) =>
  createSelector(
    selectAllCombinedLongeings,
    (combinedList: CombinedLongeingData[]) => {
      if (!recordingId || !combinedList) return null;
      return (
        combinedList.find((item) => {
          const isLeft = item.recordings?.left === recordingId;
          const isRight = item.recordings?.right === recordingId;
          const isExactCombinedMatch = item.combinedId
            ? item.combinedId.split('_').includes(recordingId)
            : false;
          return isLeft || isRight || isExactCombinedMatch;
        }) ?? null
      );
    }
  );
